// DonationSurvey utilities - extracted from presentation layer
// All utility functions, constants, and type definitions for DonationSurvey

import * as Api from '@ViewModels';
import { IRichContentEditorState } from '@ViewModels';
import * as yup from 'yup';
import {
	createBaseSurveySchema,
	createCustomFormFieldSchema,
	createNotificationSettingsSchema,
	createTagGenerationSettingsSchema,
} from '../shared/validation/schemas';

// Critical constants used across DonationSurvey components
export const QUESTION_LIMIT = 20;
export const MAX_SUGGESTED_AMOUNTS = 10;
export const MIN_DONATION_AMOUNT = 1;
export const MAX_DONATION_AMOUNT = 100000;
export const DEFAULT_SUGGESTED_AMOUNTS = [25, 50, 100, 250, 500];
export const DEFAULT_MINIMUM_AMOUNT = 1;

// Tab values for consistency with other survey types
export const TabValues = {
	Questions: 'Questions',
	Settings: 'Settings',
	Preview: 'Preview',
} as const;

export type TabValues = (typeof TabValues)[keyof typeof TabValues];

/**
 * Form structure for frequency-specific donation settings
 * Used for OneTime, Monthly, and Annual donation configurations
 */
export interface DonationFrequencyFormSettings {
	/** Whether this frequency is enabled (OneTime is always enabled) */
	enabled: boolean;
	/** Whether to show suggested donation amounts */
	hasDonationValues: boolean;
	/** Array of suggested donation amounts */
	donationValues: { value: number }[];
	/** Whether donors can enter a custom amount */
	allowCustomDonationAmount: boolean;
	/** Whether a minimum donation amount is required */
	hasMinimumDonationAmount: boolean;
	/** Minimum donation amount */
	minimumDonationAmount: number;
}

/**
 * Shared message for the 'at-least-one-frequency' validation rule. Used both as the
 * Yup schema's error message and as the toast text fired from the container's
 * onInvalid handler, so they can't drift apart.
 */
export const AT_LEAST_ONE_FREQUENCY_MESSAGE =
	'Donation surveys require at least one donation frequency. Enable One-Time, Monthly, or Annual to save.';

// Form type for DonationSurvey (following other survey patterns)
// Omits server-generated fields that aren't needed during form editing
export type DonationSurveyFormData = Omit<
	Api.IDonationSurvey,
	'_type' | 'id' | 'anonymousLink' | 'archivedDate' | 'stats'
> & {
	donationPageTitle: string;
	causeDetails: string;
	allowProgramSpecificDonations: boolean;
	requireCampaignSelection: boolean;
	programSpecificDonation: Api.IDonationCampaign[];
	hasExpirationDate: boolean;
	image: Api.IFileAttachment | null;
	imageSource: Api.IAttachmentUploadSource | null;
	updateExistingContacts: boolean;
	collectDonorPhone: DonationDonorInfoCollection;
	collectDonorAddress: DonationDonorInfoCollection;

	// Donation receipt email (labels are receipt-oriented; field names match the API contract)
	/** Whether the donation receipt email is sent to the donor after a paid donation */
	sendConfirmationEmail: boolean;
	/** Rich-text copy that replaces the default receipt narrative; blank → standard receipt */
	confirmationMessage?: IRichContentEditorState;

	// Legacy flat structure (deprecated - kept for backward compatibility during transition)
	/** @deprecated Use oneTimeSettings instead */
	hasDonationValues: boolean;
	/** @deprecated Use oneTimeSettings.donationValues instead */
	donationValues: { value: number }[];
	/** @deprecated Use oneTimeSettings.allowCustomDonationAmount instead */
	allowCustomDonationAmount: boolean;
	/** @deprecated Use oneTimeSettings.hasMinimumDonationAmount instead */
	hasMinimumDonationAmount: boolean;
	/** @deprecated Use oneTimeSettings.minimumDonationAmount instead */
	minimumDonationAmount: number;

	// New frequency-specific settings
	oneTimeSettings: DonationFrequencyFormSettings;
	monthlySettings: DonationFrequencyFormSettings;
	annualSettings: DonationFrequencyFormSettings;

	allowCustomerToCoverServiceFees: boolean;
	serviceFeeCalculationMethod: DonationServiceFeeCalculationMethod;
	serviceFeeFixedAmount: number;
	serviceFeePercentage: number;

	// Per-field conditional visibility validation entries (validation-only, stripped before save)
	surveyFormVisibilityValidation?: {
		enabled: boolean;
		hasQuestion: boolean;
		hasAllAnswers: boolean;
	}[];
};

export enum DonationDonorInfoCollection {
	COLLECT = 'collect',
	REQUIRE = 'require',
	NO_COLLECT = 'doNotCollect',
}

export enum DonationServiceFeeCalculationMethod {
	STRIPE = 'Stripe',
	FIXED = 'Fixed',
	PERCENTAGE = 'Percentage',
}

/**
 * Validation schema for frequency-specific donation settings
 * Validates that enabled frequencies have proper donation amount configuration
 */
const frequencySettingsSchema = yup
	.object()
	.shape({
		enabled: yup.boolean().required('Enabled is required'),
		hasDonationValues: yup.boolean().required('Has donation values is required'),
		donationValues: yup
			.array()
			.of(
				yup.object().shape({
					value: yup.number().positive('Donation value must be positive').required('Donation value is required'),
				})
			)
			.required('Donation values are required'),
		allowCustomDonationAmount: yup.boolean().required('Allow custom donation amount is required'),
		hasMinimumDonationAmount: yup.boolean().required('Has minimum donation amount is required'),
		// Note: Minimum amount is only shown/editable for one-time donations in the UI,
		// but validation applies to all frequencies for data integrity and backward compatibility
		minimumDonationAmount: yup.number().when('hasMinimumDonationAmount', {
			is: true,
			then: schema =>
				schema.required('Minimum donation amount is required').positive('Minimum donation amount must be positive'),
			otherwise: schema => schema.nullable(),
		}),
	})
	/**
	 * Custom validation test to ensure at least one donation method is available.
	 *
	 * Validation Rules:
	 * 1. Only validates if this frequency is enabled
	 * 2. If "Set Donation Amount" toggle is ON, must have at least one valid suggested amount
	 * 3. Must have at least ONE way to donate (suggested amounts OR custom amount)
	 *
	 * Edge Cases:
	 * - Disabled frequencies skip validation (return true early)
	 * - Amounts with all zeros fail validation even if custom amount is enabled
	 * - Empty suggested amounts array with hasDonationValues=true triggers specific error
	 * - Both suggested amounts and custom amounts can be enabled simultaneously
	 *
	 * Note: The "at least one frequency must be enabled" constraint is enforced at the root
	 * schema level via the 'at-least-one-frequency' test, not here.
	 */
	.test('has-donation-method', 'At least one donation method is required', function (value) {
		// Only validate if this frequency is enabled
		if (!value.enabled) {
			return true; // Skip validation for disabled frequencies
		}

		// First check: if "Set Donation Amount" toggle is ON, must have valid amounts
		if (value.hasDonationValues) {
			const hasValidAmounts = value.donationValues && value.donationValues.some(dv => dv.value && dv.value > 0);
			if (!hasValidAmounts) {
				return this.createError({
					path: `${this.path}.donationValues`,
					message: 'Please add at least one suggested amount',
				});
			}
		}

		// Second check: must have at least one donation method overall (suggested amounts OR custom amount)
		const hasValidSuggestedAmounts =
			value.hasDonationValues && value.donationValues && value.donationValues.some(dv => dv.value && dv.value > 0);
		const hasCustomAmount = value.allowCustomDonationAmount;

		if (!hasValidSuggestedAmounts && !hasCustomAmount) {
			return this.createError({
				path: `${this.path}.donationValues`,
				message: 'Please add suggested amounts or enable custom donation amounts',
			});
		}

		return true;
	});

// Validation schema for DonationSurvey using shared schemas
export const validationSchema = yup
	.object({
		// Base survey fields using shared schema
		...createBaseSurveySchema({
			requireIntro: false,
			requireStartDate: false,
			requireExpirationDate: true,
		}),
		donationPageTitle: yup.string().required('Donation page title is required'),
		causeDetails: yup.string().required('Cause details is required'),
		allowProgramSpecificDonations: yup.boolean().required('Allow program specific donations is required'),
		requireCampaignSelection: yup.boolean().required('Require campaign selection is required'),
		hasExpirationDate: yup.boolean().required('Has expiration date is required'),
		updateExistingContacts: yup.boolean().required('Update existing contacts is required'),
		collectDonorPhone: yup.string().required('Collect donor phone is required'),
		collectDonorAddress: yup.string().required('Collect donor address is required'),

		// Legacy fields (deprecated but kept for backward compatibility)
		hasDonationValues: yup.boolean().required('Has donation values is required'),
		donationValues: yup
			.array()
			.of(
				yup.object().shape({
					value: yup.number().positive('Donation value must be positive').required('Donation value is required'),
				})
			)
			.required('Donation values are required'),
		allowCustomDonationAmount: yup.boolean().required('Allow custom donation amount is required'),
		hasMinimumDonationAmount: yup.boolean().required('Has minimum donation amount is required'),
		minimumDonationAmount: yup.number().when('hasMinimumDonationAmount', {
			is: true,
			then: schema =>
				schema.required('Minimum donation amount is required').positive('Minimum donation amount must be positive'),
			otherwise: schema => schema.nullable(),
		}),

		// New frequency-specific settings
		oneTimeSettings: frequencySettingsSchema.required('One-time settings are required'),
		monthlySettings: frequencySettingsSchema.required('Monthly settings are required'),
		annualSettings: frequencySettingsSchema.required('Annual settings are required'),
	})
	.test(
		'at-least-one-frequency',
		AT_LEAST_ONE_FREQUENCY_MESSAGE,
		function (value) {
			const oneTimeEnabled = value?.oneTimeSettings?.enabled;
			const monthlyEnabled = value?.monthlySettings?.enabled;
			const annualEnabled = value?.annualSettings?.enabled;
			if (!oneTimeEnabled && !monthlyEnabled && !annualEnabled) {
				return this.createError({
					path: 'oneTimeSettings.enabled',
					message: AT_LEAST_ONE_FREQUENCY_MESSAGE,
				});
			}
			return true;
		}
		// Re-open the schema object for the remaining fields by continuing the yup.object chain.
		// We reassign the schema variable below; the fields below are appended via .shape().
	)
	.shape({
		sendConfirmationEmail: yup.boolean().optional().default(true),
		confirmationMessage: yup.mixed<IRichContentEditorState>().optional(),

		allowCustomerToCoverServiceFees: yup.boolean().required('Allow customer to cover service fees is required'),
		serviceFeeCalculationMethod: yup.string().required('Service fee calculation method is required'),
		serviceFeeFixedAmount: yup.number().required('Service fee fixed amount is required'),
		serviceFeePercentage: yup.number().required('Service fee percentage is required'),

		customForm: yup.object({
			name: yup.string(),
			startDate: yup.date(),
			fields: createCustomFormFieldSchema({
				requireLabels: false, // CustomSurvey allows flexible label requirements
			}),
		}),
		tagGenerationSettings: createTagGenerationSettingsSchema().optional(),
		notificationSettings: createNotificationSettingsSchema().optional(),

		// SurveyForm — no schema validation needed, validated via surveyFormVisibilityValidation
		surveyForm: yup.mixed().optional(),

		// Per-field conditional visibility validation entries
		// Parallel array matching customForm.fields, used to validate incomplete visibility configs
		surveyFormVisibilityValidation: yup
			.array()
			.of(
				yup
					.object({
						enabled: yup.boolean(),
						hasQuestion: yup.boolean(),
						hasAllAnswers: yup.boolean(),
					})
					.test(
						'conditional-visibility-complete',
						'Please select a question and answer for conditional visibility',
						function (value) {
							if (!value?.enabled) return true;
							if (!value.hasQuestion) {
								return this.createError({ message: 'Please select a question for conditional visibility' });
							}
							if (!value.hasAllAnswers) {
								return this.createError({ message: 'Please select an answer for each conditional visibility row' });
							}
							return true;
						}
					)
			)
			.optional(),
	});
